import React, { useEffect, useRef, useState } from "react";
import activeMic from "../../assets/ActiveMic.png";
import inActiveMic from "../../assets/InActiveMic.png";
import './voiceToText.scss';

const SpeechRecognition =
  (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;

interface VoiceToTextProps {
  transcript: string;
  onTranscriptChange: (text: string) => void;
  lang?: string;
  tabIndex?: number;
}

const VoiceToText: React.FC<VoiceToTextProps> = ({
  transcript,
  onTranscriptChange,
  lang = "en-US",
  tabIndex,
}) => {
  const [isListening, setIsListening] = useState(false);
  const [showLabel, setShowLabel] = useState(false);

  const recognitionRef = useRef<any | null>(null);
  const isListeningRef = useRef<boolean>(false);
  const finalTranscriptRef = useRef<string>("");
  const onTranscriptChangeRef = useRef(onTranscriptChange);

  useEffect(() => {
    onTranscriptChangeRef.current = onTranscriptChange;
  }, [onTranscriptChange]);

  // reset finalTranscript if textarea cleared manually
  useEffect(() => {
    if (transcript?.trim() === "") {
      finalTranscriptRef.current = "";
    }
  }, [transcript]);

  useEffect(() => {
    if (!SpeechRecognition) {
      console.error(
        "Speech Recognition API not supported in this browser. Use Chrome/Edge on HTTPS or localhost."
      );
      return undefined;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = lang;

    recognition.onresult = (event: any) => {
      let interimTranscript = "";
      let newFinal = finalTranscriptRef.current;

      for (let i = event.resultIndex; i < event.results.length; ++i) {
        const result = event.results[i];
        const text = result[0]?.transcript ?? "";
        if (result.isFinal) {
          newFinal += text + " ";
        } else {
          interimTranscript += text;
        }
      }

      finalTranscriptRef.current = newFinal;
      onTranscriptChangeRef.current(newFinal + interimTranscript);
    };

    recognition.onerror = (err: any) => {
      console.error("Speech recognition error:", err);
      isListeningRef.current = false;
      setIsListening(false);
    };

    recognition.onend = () => {
      if (isListeningRef.current) {
        try {
          recognition.start();
        } catch (e) {
          console.warn("Could not restart recognition:", e);
        }
      }
    };

    recognitionRef.current = recognition;

    return () => {
      try {
        recognition.stop();
      } catch (e) {
        console.debug("Recognition already stopped:", e);
      }
      recognitionRef.current = null;
    };
  }, [lang]);

  useEffect(() => {
    isListeningRef.current = isListening;
  }, [isListening]);

  const toggleListening = async () => {
    const recognition = recognitionRef.current;
    if (!recognition) return;

    if (isListeningRef.current) {
      try {
        recognition.stop();
      } catch (e) {
        console.debug("Stop recognition failed:", e);
      }
      isListeningRef.current = false;
      setIsListening(false);
    } else {
      try {
        finalTranscriptRef.current = transcript.trim() === "" ? "" : transcript;

        recognition.start();
        isListeningRef.current = true;
        setIsListening(true);
      } catch (e: any) {
        console.error("Failed to start speech recognition:", e);
        isListeningRef.current = false;
        setIsListening(false);
      }
    }
  };

  return (
    <div
      className="voice-to-text-wrapper"
      onMouseEnter={() => setShowLabel(true)}
      onMouseLeave={() => setShowLabel(false)}
    >
      <button
        type="button"
        className="icon-btn"
        onClick={toggleListening}
        aria-label={isListening ? "Stop Voice Input" : "Start Voice Input"}
        tabIndex={tabIndex}
      >
        <img
          src={isListening ? activeMic : inActiveMic}
          alt="Mic"
          style={{ width: 32, height: 32 }}
        />
      </button>

      {showLabel && (
        <div className="voice-to-text-hover-label">
          {isListening ? "End Voice-to-text" : "Start Voice-to-text"}
        </div>
      )}
    </div>
  );
};

export default VoiceToText;